import CurrencyEnum from "@/app/shared/CurrencyEnum.js";
import vSelect from 'vue-select';
import ElementEnum from "@/app/shared/ElementEnum";
import i18n from "@/i18n/i18n";

export default {
  components: {
    vSelect
  },
  props: {
    title: {
      type: String,
      required: true
    },
    refDate: {
      type: Date,
      required: true
    },
    forecastType: {
      type: Number,
      required: true
    }
  },
  mounted() {
    // this.$refs.listCard.refreshcard();
    this.initProjectId();
    this.getAvailableElements();
    this.getNSRForecast();
  },
  data() {
    return {
      // Common stuff
      projectId: null,

      // Visual properties
      collapsed: false,

      // Money stuff
      exchangeCurrencyOptions: CurrencyEnum.definition,

      // Element properties
      availableElements: null,
      elementOptions: ElementEnum.definition,
      elementProps: [],

      nsr: {
        // NSR Properties
        id: 0,

        // NSR State
        isLocked: false,
        lockedBy: null,
        lockedDate: null,

        // NSR Parameters
        cuAuRefineryCostPerTon: null,
        znRefineryCostPerTon: null,
        lowZnGradeAdditionalRefineryCostPerMetricTon: null,
        highZnGradeAdditionalRefineryCostPerMetricTon: null,
        highZnMinimumGrade: null,

        // Elements data:
        //  - element: ElementEnum
        //  - price: number
        //  - recoveryPct: number
        elements: [],
      
        // Currency exchange rate
        fxCurrency: null,
        fxRate: 0
      }
      
    }
  },
  watch: {
    refDate() {
      this.getNSRForecast();
    }
  },
  computed: {
    lockedString() {
      const dateObj = (new Date(this.nsr.lockedDate));
      const date = dateObj.toLocaleDateString('fr-CA'); // Because this returns yyyy-mm-dd
      const time = dateObj.toLocaleTimeString('en-UK'); // Because this returns hh:mm:ss 24H format
      return i18n.t("GradeControl.NSRForecast.LockedBy", { user: this.nsr.lockedBy, date, time });
    }
  },
  methods: {
    lock() {
      this.$cqrsPost("geo/gradeControl/NetSmelterReturnForecast/lock", { id: this.nsr.id })
        .then((response) => {
          this.$notifySaveSuccess();
          this.nsr = response.data.model;
        });
    },
    unlock() {
      this.$cqrsPost("geo/gradeControl/NetSmelterReturnForecast/unlock", { id: this.nsr.id })
        .then((response) => {
          this.$notifySaveSuccess();
          this.nsr = response.data.model;
        });
    },
    initProjectId() {
      this.projectId = Number(this.$store.state.projectId);
    },

    getAvailableElements() {
      /*
        Alright so we expect the backend to return only elements that are available for the selected project.
        However when the component is mounted, or when we need to create a new NSR we need a way to keep track
        of which elements are available for the selected project.

        The avaiableElements property is used to keep track of the elements that are available for the selected project.
        Every time we empty the nsr, we can rebuild the visual with the known elements.

        I hope it makes sense, if not ping me. aguillemette@alliestech.com
        - Aub
      */
      const params = {
        projectId: this.projectId
      };

      this.$cqrsGet("core/projectElements/Find", { params })
        .then((response) => {
          const data = response.data.model;
          this.availableElements = data.map(x => {
            return {
              element: x,
              price: null,
              recoveryPct: null
            }
          });
          this.nsr.elements = this.cloneObjArray(this.availableElements); // No reference, thank you ciao pow
          this.feedElementMetainfo();
        })
        .catch((error) => {
          this.$notifyLoadingError(error.message);
        });
    },

    getNSRForecast() {
      if (!this.refDate) return;
      if (!this.projectId) return;

      const params = {
        projectId: this.projectId,
        refDate: this.refDate,
        forecastType: this.forecastType
      };

      // For the UI
      this.$emit("refreshing");

      this.$cqrsGet("geo/gradeControl/NetSmelterReturnForecast/CheckIfExists", { params })
        .then((response) => {
          const id = response.data.model;
          if (!id) {
            this.emptyNewNsr();
            return;
          }

          this.$cqrsGet("geo/gradeControl/NetSmelterReturnForecast/FindById", { params: { id } })
            .then((response) => {
              this.nsr = response.data.model;
              this.feedElementMetainfo();
            })
            .catch((error) => {
              this.$notifyLoadingError(error.message);
            })
            .finally(() => {
              // For the UI
              this.$emit("stopRefreshing");
            });
        })
        .finally(() => {
          this.$emit("stopRefreshing");
        });
    },
    emptyNewNsr() {
      this.nsr.id = 0;
      
      this.nsr.isLocked = false;
      this.nsr.lockedBy = null;
      this.nsr.lockedDate = null;

      this.nsr.cuAuRefineryCostPerTon = null;
      this.nsr.znRefineryCostPerTon = null;
      this.nsr.lowZnGradeAdditionalRefineryCostPerMetricTon = null;
      this.nsr.highZnGradeAdditionalRefineryCostPerMetricTon = null;
      this.nsr.highZnMinimumGrade = null;

      // This is kinda wack, please read the comment in the getAvailableElements method
      this.nsr.elements = this.cloneObjArray(this.availableElements);
      this.feedElementMetainfo();
      
      this.nsr.fxCurrency = null;
      this.nsr.fxRate = 0;
    },

    saveNsr() {
      this.nsr.projectId = this.projectId;
      this.nsr.refDate = this.refDate;
      this.nsr.nsrForecastType = this.forecastType;
      const url = this.nsr.id === 0
        ? "geo/gradeControl/NetSmelterReturnForecast/Create"
        : "geo/gradeControl/NetSmelterReturnForecast/Edit";
      this.$cqrsPost(url, { netSmelterReturnForecast: this.nsr })
        .then((response) => {
          this.nsr = response.data.model;
          this.feedElementMetainfo();
          this.$notifySaveSuccess();
        })
        .catch((error) => {
          this.$notifySaveError(error.message);
        });
    },

    cloneObjArray(arr) {
      if (!arr) return null;
      return arr.map(x => ({ ...x }));
    },

    feedElementMetainfo() {
      if (!this.nsr.elements) return null;
      this.nsr.elements = this.nsr.elements.map(x => {
        return {
          ...x,
          name: this.elementOptions[x.element].name,
          unit: this.elementUnit(x.element)
        }
      });
    },

    elementUnit(element) {
      switch (element) {
      case 0:
      case 1:
        return "$ US / oz";
      case 2:
      case 3:
      case 4:
        return "$ US / lb";
      default:
        return "";
      }
    }
  }
}